import {
  createRng,
  IllegalMoveError,
  type GameEngine,
  type GameOptions,
  type RNG,
  type ScoreResult,
  type Seat,
} from "@bing/engine-core";
import {
  captureAt,
  destinationFor,
  hasWon,
  legalTokens,
  mayRollAgain,
  nextSeat,
  setupTokens,
  tokensHome,
  yardLocked,
} from "./rules";
import {
  DEFAULT_MENSCH_CONFIG,
  FINAL_STEP,
  MENSCH_SEATS,
  RELEASE_ROLL,
  TOKENS_PER_PLAYER,
  TRACK_LEN,
  YARD,
  type MenschConfig,
  type MenschEvent,
  type MenschMove,
  type MenschState,
  type MenschView,
} from "./types";

export { MENSCH_SEATS } from "./types";

/**
 * How long a rolled die is left on the table before the mover plays it.
 *
 * Must exceed the CLIENT's throw animation (1000ms — `DIE_THROW_MS` in
 * `apps/web/src/game/backgammon-dice.tsx`) and then leave real time to READ the number.
 * These two constants are a contract: shorten this and bot rolls stop being watchable.
 */
export const ROLL_READ_MS = 2200;

/* ---- MOVEMENT PACING -------------------------------------------------------------------
 * The client WALKS a token square by square rather than sliding it straight across the
 * board, because counting the squares is how you actually read what happened. These beats
 * must cover that walk: shorten them and the next player acts while a token is still moving.
 * Client half of the contract: `STEP_HOP_MS` / `TOKEN_SLIDE_MS` in screens/MenschTable.tsx.
 * ---------------------------------------------------------------------------------------- */

/** Per square of the walk. */
export const STEP_HOP_MS = 190;
/**
 * Breath after an ordinary move lands, BEFORE the next player throws.
 *
 * This is the "move → next throw" gap. Together with {@link ROLL_READ_MS} (the "throw →
 * move" gap) it sets the whole rhythm of a bot's turn: settle → throw (1s of tumble) → read
 * the number → walk the token → settle. Tuned so a bot turn is watchable rather than a blur;
 * they are the single knob to turn if the game ever feels slow with four players.
 */
export const MOVE_SETTLE_MS = 800;
/** A capture also has a token flying home and an announcement to read. */
export const CAPTURE_SETTLE_MS = 1600;
/** A token reaching home is worth a moment. */
export const FINISH_SETTLE_MS = 1100;

/** Clamp any inbound seat count to a real منچ table (2..4). */
function clampPlayers(n: unknown): number {
  const v = typeof n === "number" && Number.isFinite(n) ? Math.floor(n) : MENSCH_SEATS;
  return Math.min(MENSCH_SEATS, Math.max(2, v));
}

function configFrom(opts: GameOptions): MenschConfig {
  const r = (opts.rules ?? {}) as Partial<MenschConfig>;
  return {
    // The table's seat count IS the player count; an explicit rules override wins so a
    // test (or a future host setting) can pin it independently of the room's seats.
    playerCount: clampPlayers(r.playerCount ?? opts.seats),
    mandatoryCapture: r.mandatoryCapture ?? DEFAULT_MENSCH_CONFIG.mandatoryCapture,
    yardRollAttempts: r.yardRollAttempts ?? DEFAULT_MENSCH_CONFIG.yardRollAttempts,
    safeStartSquare: r.safeStartSquare ?? DEFAULT_MENSCH_CONFIG.safeStartSquare,
  };
}

/** Hand the turn to the next seat and clear the per-turn roll state. */
function passTurn(state: MenschState): MenschState {
  return {
    ...state,
    turn: nextSeat(state.turn, state.tokens.length),
    die: null,
    rollsUsed: 0,
  };
}

/**
 * منچ (Mensch ärgere Dich nicht / Ludo) — pure engine.
 *
 * Turn shape: `roll` → `move`, or `pass` when a non-6 roll cannot be played. Rolling a
 * 6 ALWAYS grants another roll (R6) — after it is played, AND when it produces no legal
 * move at all (the turn stays with the roller, who rolls again). There is NO cap: six →
 * roll again, six again → roll again, and so on. The turn ends only when a NON-6 is rolled
 * (played, or passed if it has no move) — which, being 5/6 of rolls, happens almost surely.
 * While every token is still in the yard the player may re-roll looking for a 6, up to
 * `yardRollAttempts` (V1).
 */
export const menschEngine: GameEngine<MenschState, MenschMove, MenschView> = {
  gameId: "mensch",

  createInitialState(opts: GameOptions, rng: RNG): MenschState {
    const config = configFrom(opts);
    return {
      tokens: setupTokens(config.playerCount),
      turn: 0,
      die: null,
      rollsUsed: 0,
      rollCount: 0,
      moveCount: 0,
      rngState: rng.getState(),
      winner: null,
      config,
      lastEvent: null,
      lastAction: null,
    };
  },

  legalMoves(state: MenschState, seat: Seat): MenschMove[] {
    if (state.winner !== null || seat !== state.turn) return [];
    if (state.die === null) return [{ kind: "roll" }];
    const tokens = legalTokens(state, seat, state.die);
    if (tokens.length > 0) return tokens.map((token) => ({ kind: "move", token }));
    // Nothing playable. R6 — an unplayable 6 STILL earns another roll (no cap); the turn does NOT
    // pass. A non-6 dud passes (or, while yard-locked with attempts left, re-rolls — V1).
    if (state.die === RELEASE_ROLL) return [{ kind: "roll" }];
    if (mayRollAgain(state, seat, state.config)) return [{ kind: "roll" }];
    return [{ kind: "pass" }];
  },

  isLegal(state: MenschState, seat: Seat, move: MenschMove): boolean {
    return this.legalMoves(state, seat).some(
      (m) =>
        m.kind === move.kind &&
        (m.kind !== "move" || m.token === (move as { token: number }).token),
    );
  },

  applyMove(state: MenschState, seat: Seat, move: MenschMove): MenschState {
    if (!this.isLegal(state, seat, move)) {
      throw new IllegalMoveError(seat, move);
    }
    const rng = createRng(state.rngState);

    if (move.kind === "roll") {
      const die = rng.range(1, 6);
      return {
        ...state,
        die,
        rollsUsed: state.rollsUsed + 1,
        // Monotonic: two identical rolls in a row are still two throws to animate.
        rollCount: state.rollCount + 1,
        rngState: rng.getState(),
        lastAction: "roll",
      };
    }

    if (move.kind === "pass") {
      return passTurn({ ...state, lastEvent: null, lastAction: "pass" });
    }

    // A checker move. `die` is non-null and the destination legal (isLegal above).
    const die = state.die as number;
    const from = state.tokens[seat]?.[move.token] as number;
    const to = destinationFor(state, seat, move.token, die) as number;
    const victim = captureAt(state, seat, to);

    const tokens = state.tokens.map((row, s) =>
      row.map((step, t) => {
        if (s === seat && t === move.token) return to;
        // R7 — the captured token goes all the way back to its owner's yard.
        if (victim && s === victim.seat && t === victim.token) return YARD;
        return step;
      }),
    );

    const event: MenschEvent = {
      seat,
      token: move.token,
      from,
      to,
      released: from === YARD,
      finished: to === FINAL_STEP,
      // Crossed off the track into the home row — true for ALL FOUR home squares.
      enteredHome: from < TRACK_LEN && to >= TRACK_LEN,
      ...(victim ? { captured: victim } : {}),
    };

    const moved: MenschState = {
      ...state,
      tokens,
      lastEvent: event,
      lastAction: "move",
      moveCount: state.moveCount + 1,
      rngState: rng.getState(),
    };

    if (hasWon(moved, seat)) {
      return { ...moved, winner: seat, die: null };
    }
    // R6 — a played 6 earns another roll; R12 — a capture does not.
    if (die === RELEASE_ROLL) {
      return { ...moved, die: null, rollsUsed: 0 };
    }
    return passTurn(moved);
  },

  isTerminal(state: MenschState): boolean {
    return state.winner !== null;
  },

  score(state: MenschState): ScoreResult {
    const seatScores = Array.from({ length: state.tokens.length }, (_, s) => tokensHome(state, s));
    return {
      seatScores,
      winners: state.winner === null ? [] : [state.winner],
    };
  },

  viewFor(state: MenschState, seat: Seat): MenschView {
    const isMover = seat === state.turn && state.winner === null;
    const isSix = state.die === RELEASE_ROLL;
    const tokens = state.die !== null && isMover ? legalTokens(state, seat, state.die) : [];
    // An unplayable 6: the turn stays with the roller, who must roll AGAIN (a 6 never passes).
    const mustReroll = isMover && isSix && tokens.length === 0;
    return {
      you: seat,
      turn: state.turn,
      tokens: state.tokens,
      die: state.die,
      legalTokens: tokens,
      canRoll: isMover && (state.die === null || mustReroll),
      noLegalMove: isMover && state.die !== null && tokens.length === 0 && !mustReroll,
      mustReroll,
      rollCount: state.rollCount,
      moveCount: state.moveCount,
      // The die on the table belongs to whoever is on turn; none is showing before a throw.
      roller: state.die === null ? null : state.turn,
      finished: Array.from({ length: state.tokens.length }, (_, s) => tokensHome(state, s)),
      status: state.winner === null ? "playing" : "over",
      ...(state.winner !== null ? { winner: state.winner } : {}),
      lastEvent: state.lastEvent,
    };
  },

  /** Let the client watch a capture / release land before the next bot acts. */
  postMoveDelayMs(state: MenschState): number | undefined {
    // A THROW gets its own, longer beat: the die must finish tumbling AND be readable before
    // the token moves. Without this the roll and the move ran back-to-back, so a bot's die was
    // gone before its animation had played — the roll read as instant and un-animated.
    if (state.lastAction === "roll") return ROLL_READ_MS;
    const e = state.lastEvent;
    if (!e) return undefined;
    // Scaled to the DISTANCE actually travelled: a six is a longer walk than a one, and the
    // pause has to cover it. A release pops straight onto the start square, so it is one hop.
    const squares = e.released ? 1 : Math.max(1, e.to - e.from);
    const walk = squares * STEP_HOP_MS;
    if (e.captured) return walk + CAPTURE_SETTLE_MS;
    if (e.finished) return walk + FINISH_SETTLE_MS;
    return walk + MOVE_SETTLE_MS;
  },
};

export { TOKENS_PER_PLAYER, YARD, FINAL_STEP, yardLocked };
